/**
 * ProfileAvatarSection — 2026-02-20 (Profile Photo Upload)
 *
 * Circular avatar + upload / remove controls rendered at the top of the
 * Settings → Profile section. Self-serve for every role. Backend:
 *   POST   /api/users/me/avatar   (multipart, JPG/PNG ≤ 5 MB)
 *   DELETE /api/users/me/avatar
 *
 * After upload the `users.avatar_url` field updates; every place that
 * reads it (sidebar, assignee chips, attendance list, activity feed)
 * picks up the new photo on next fetch. The `stellar-avatar-updated`
 * window event nudges the immediate UI (sidebar avatar) to refresh
 * without a full reload.
 */
import { useRef, useState } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Camera, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

function initialsOf(name, email) {
  const seed = (name || email || '?').trim();
  const parts = seed.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (parts[0] || '?').slice(0, 2).toUpperCase();
}

export default function ProfileAvatarSection({ user, onChanged }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(user?.avatar_url || '');

  const handlePick = () => fileRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    // Client-side validation (server-side validates too).
    const okExt = /\.(jpe?g|png)$/i.test(file.name);
    if (!okExt) {
      toast.error('Only JPG and PNG files are allowed');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Photo must be 5 MB or smaller');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/users/me/avatar', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = r?.data?.avatar_url || '';
      setPreviewUrl(url);
      toast.success('Photo updated');
      onChanged?.(url);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    setRemoving(true);
    try {
      await api.delete('/users/me/avatar');
      setPreviewUrl('');
      toast.success('Photo removed');
      onChanged?.('');
    } catch {
      toast.error('Failed to remove photo');
    } finally {
      setRemoving(false);
    }
  };

  const initials = initialsOf(user?.name, user?.email);
  const hasPhoto = !!previewUrl;

  return (
    <div className="flex items-center gap-4 pb-4 border-b border-border/15" data-testid="settings-profile-avatar-section">
      <Avatar className="h-20 w-20 ring-2 ring-border/20" data-testid="settings-profile-avatar">
        {hasPhoto && <AvatarImage src={previewUrl} alt={user?.name || 'avatar'} />}
        <AvatarFallback className="bg-foreground/[0.06] text-foreground text-[18px] font-semibold">{initials}</AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-medium text-foreground truncate">{user?.name || user?.email || ''}</div>
        <div className="text-[11px] text-muted-foreground/60">
          {hasPhoto
            ? 'Your photo shows everywhere your initials show today.'
            : 'Add a photo so teammates can recognise you at a glance.'}
        </div>
        <div className="flex items-center gap-2 mt-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,.jpg,.jpeg,.png"
            className="hidden"
            onChange={handleFile}
            data-testid="settings-profile-avatar-file-input"
          />
          <Button
            type="button"
            size="sm"
            onClick={handlePick}
            disabled={uploading || removing}
            className="bg-gold/10 hover:bg-gold/20 text-gold border-0 gap-1.5"
            data-testid="settings-profile-avatar-upload-btn"
          >
            {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Camera className="h-3 w-3" />}
            {uploading ? 'Uploading…' : (hasPhoto ? 'Change photo' : 'Upload photo')}
          </Button>
          {hasPhoto && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={handleRemove}
              disabled={uploading || removing}
              className="text-muted-foreground/70 hover:text-red-400 gap-1.5"
              data-testid="settings-profile-avatar-remove-btn"
            >
              {removing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
              Remove
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
